/**
 * P2-S6 관찰 메모·성취(FS-11-04 · SR-TCH-04 · SR-INT-03 — 계약 §3.3 · 세션 역할 teacher · 코스 경계):
 *   GET /api/courses/{c}/teacher/assess?activityId= → { activity:{id,title,autoComplete,agsLinked}, activities:[{id,title}],
 *        students:[{ pid, nickname, today:{finishes,tries,visits,pushout,carry}, level 1|2|3|null, suggested 2|null, note,
 *        ags: none|pending|sent|failed|retrying }], counts:{ entered, total, agsSent, agsPending } }
 *        — 활동을 주지 않으면 열린 룸의 활동(없으면 가장 최근 활동)
 *   PUT /api/courses/{c}/teacher/assess/{pid} { activityId, note?(≤2000), level?(1|2|3|null) } → 교사 메모 upsert · level 있으면 AGS 대기
 *        · 422 필드 · 학생 403(+ 감사)
 * 오늘 활동(today)은 교사 참고용 — 성취에 자동 반영되지 않는다. 제안(suggested)은 교사가 저장해야 AGS 로 간다.
 * 점수·순위 필드 없음 — 셸은 이름순으로만 줄 세운다.
 */
import { useMemo } from "react";
import { ApiError } from "../../../lib/http/errors";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";
import type { Requester } from "../../../types/api";
import type { Level, AgsState, AssessStudent, AssessView, AssessSave } from "../types";

/** 메모 최대 글자(계약 §3.3) */
export const NOTE_MAX = 2000;

/** 422 — 서버가 받지 않은 필드(note · level · activityId) */
export class AssessRejected extends Error {
  constructor(readonly field: string) {
    super(`rejected: ${field}`);
    this.name = "AssessRejected";
  }
}

export interface AssessApi {
  view(activityId?: string | null): Promise<AssessView>;
  save(pid: string, body: AssessSave): Promise<void>;
}

const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
const count = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};
const AGS: readonly AgsState[] = ["none", "pending", "sent", "failed", "retrying"];

export const isLevel = (v: unknown): v is Level => v === 1 || v === 2 || v === 3;

function readStudent(v: unknown): AssessStudent | null {
  const o = obj(v);
  if (!o || typeof o.pid !== "string") return null;
  const d = obj(o.today) ?? {};
  const ags = str(o.ags);
  return {
    pid: o.pid,
    nickname: str(o.nickname) ?? "—",
    today: {
      finishes: count(d.finishes),
      tries: count(d.tries),
      visits: count(d.visits),
      pushout: count(d.pushout),
      carry: count(d.carry),
    },
    level: isLevel(o.level) ? o.level : null,
    suggested: o.suggested === 2 ? 2 : null,
    note: str(o.note) ?? "",
    ags: ags && (AGS as readonly string[]).includes(ags) ? (ags as AgsState) : "none",
  };
}

/** 응답을 너그럽게 읽는다 — 모르는 필드는 버리고 모양이 어긋난 학생은 건너뛴다. counts 가 없으면 셸이 센다 */
export function readAssess(v: unknown): AssessView {
  const o = obj(v) ?? {};
  const a = obj(o.activity);
  const students = (Array.isArray(o.students) ? o.students : [])
    .map(readStudent)
    .filter((s): s is AssessStudent => !!s);
  const c = obj(o.counts);
  return {
    activity:
      a && typeof a.id === "string"
        ? {
            id: a.id,
            title: str(a.title) ?? a.id,
            autoComplete: a.autoComplete === true,
            agsLinked: a.agsLinked === true,
          }
        : null,
    activities: (Array.isArray(o.activities) ? o.activities : [])
      .map(obj)
      .filter((x): x is Record<string, unknown> => !!x && typeof x.id === "string")
      .map((x) => ({ id: String(x.id), title: str(x.title) ?? String(x.id) })),
    students,
    counts: c
      ? {
          entered: count(c.entered),
          total: count(c.total),
          agsSent: count(c.agsSent),
          agsPending: count(c.agsPending),
        }
      : {
          entered: students.filter((s) => s.level !== null).length,
          total: students.length,
          agsSent: students.filter((s) => s.ags === "sent").length,
          agsPending: students.filter((s) => s.ags === "pending" || s.ags === "retrying").length,
        },
  };
}

export function createAssessApi(request: Requester, courseId: string): AssessApi {
  const c = `/api/courses/${encodeURIComponent(courseId)}/teacher/assess`;
  return {
    view: async (activityId) =>
      readAssess(
        await request<unknown>(
          activityId ? `${c}?activityId=${encodeURIComponent(activityId)}` : c,
        ),
      ),
    async save(pid, body) {
      try {
        await request<unknown>(`${c}/${encodeURIComponent(pid)}`, {
          method: "PUT",
          body: JSON.stringify(body),
          headers: { "Content-Type": "application/json" },
        });
      } catch (e) {
        if (e instanceof ApiError && e.status === 422) {
          const b = obj(e.body);
          throw new AssessRejected(str(b?.field) ?? str(b?.error) ?? "body");
        }
        throw e;
      }
    },
  };
}

export function useAssessApi(): AssessApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const courseId =
    state.status === "ready" && state.session.role === "teacher" ? state.session.courseId : null;
  return useMemo(
    () => (courseId ? createAssessApi(manager.request, courseId) : null),
    [manager, courseId],
  );
}
